import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { listCentersPublic } from '../../services/centers'
import MobileBottomNav from '../../components/MobileBottomNav'
import {
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  TicketIcon,
  ArrowRightIcon,
  CalendarIcon,
  TagIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline'

const CentersListPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [user, setUser] = useState(null)
  const [centers, setCenters] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filteredCenters, setFilteredCenters] = useState([])
  const displayName = user?.name || user?.email || 'Attendee'

  useEffect(() => {
    const u = getUser()
    setUser(u)
  }, [])

  const fetchCenters = async () => {
    setLoading(true)
    // For authenticated users in attendee dashboard, show all centers (including inactive)
    // For public access, show only active centers
    const currentUser = getUser()
    const includeInactive = currentUser ? 'true' : 'false'
    const res = await listCentersPublic({ includeInactive })
    if (res.status === 200) {
      setCenters(res.data || [])
      setFilteredCenters(res.data || [])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchCenters()
  }, [])

  useEffect(() => {
    if (search) {
      const filtered = centers.filter(center =>
        center.organization?.toLowerCase().includes(search.toLowerCase()) ||
        center.state?.toLowerCase().includes(search.toLowerCase()) ||
        center.localGovernment?.toLowerCase().includes(search.toLowerCase())
      )
      setFilteredCenters(filtered)
    } else {
      setFilteredCenters(centers)
    }
  }, [search, centers])

  const menuItems = [
    { 
      id: 'events', 
      label: 'Discover Events', 
      icon: CalendarIcon,
      path: '/events',
      description: 'Browse and book tickets for upcoming events',
      color: 'from-purple-500 to-purple-600'
    },
    { 
      id: 'offers', 
      label: 'Browse Offers', 
      icon: TagIcon,
      path: '/offers',
      description: 'Find exclusive deals and discounts',
      color: 'from-cyan-500 to-cyan-600'
    },
    { 
      id: 'centers', 
      label: 'Centers', 
      icon: BuildingOffice2Icon,
      path: '/centers',
      description: 'Browse centers and their profiles',
      color: 'from-blue-500 to-blue-600'
    },
    { 
      id: 'tickets', 
      label: 'My Tickets', 
      icon: TicketIcon,
      path: '/account/tickets',
      description: 'View and manage your purchased tickets',
      color: 'from-green-500 to-green-600'
    },
    { 
      id: 'orders', 
      label: 'Order History', 
      icon: ShoppingBagIcon,
      path: '/account/orders',
      description: 'Track all your transactions',
      color: 'from-orange-500 to-orange-600'
    },
    { 
      id: 'disputes', 
      label: 'Disputes', 
      icon: ExclamationTriangleIcon,
      path: '/account/disputes',
      description: 'Manage and resolve issues',
      color: 'from-red-500 to-red-600'
    },
    { 
      id: 'profile', 
      label: 'Profile', 
      icon: UserCircleIcon,
      path: '/account/profile',
      description: 'Update your account information',
      color: 'from-slate-500 to-slate-600'
    }
  ]

  // Determine active item based on current path
  const getActiveItem = () => {
    const currentPath = location.pathname
    // Check for centers paths
    if (currentPath.startsWith('/centers')) {
      return 'centers'
    }
    return menuItems.find(item => currentPath.startsWith(item.path))?.id || 'events'
  }

  const activeItemId = getActiveItem()

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      {/* Mobile Header - Instagram Style */}
      <header className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-600 to-cyan-500 flex items-center justify-center text-white">
              <TicketIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900">Obule</span>
          </div>
          {user && (
            <Link to="/account/profile" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
              <UserCircleIcon className="w-5 h-5 text-slate-600" />
            </Link>
          )}
        </div>
      </header>

      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-600 to-cyan-500 flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-105">
                <TicketIcon className="w-5 h-5" />
              </div>
              <span className="text-xl font-bold text-slate-900 tracking-tight">Obule</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = activeItemId === item.id
              
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-cyan-50 text-cyan-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-cyan-600' : 'text-slate-400'}`} />
                  {item.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          {user && (
            <div className="p-4 border-t border-slate-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                  <UserCircleIcon className="w-6 h-6" />
                </div>
                <div className="overflow-hidden">
                  <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  logout()
                  navigate('/login')
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col pb-20 lg:pb-0">
        {/* Content - Instagram Feed Style */}
        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="max-w-7xl mx-auto">
            {/* Welcome Header - Mobile */}
            <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-6 mb-4">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">
                Event Centers
              </h1>
              <p className="text-slate-600 text-sm">
                Browse venues, restaurants, and entertainment spots
              </p>
            </div>

            {/* Desktop Welcome */}
            <div className="hidden lg:block mb-8 px-4 lg:px-8 pt-8">
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3">
                Event Centers
              </h1>
              <p className="text-lg text-slate-600">
                Browse venues, restaurants, and entertainment spots near you
              </p>
            </div>

            {/* Search Bar */}
            <div className="px-4 lg:px-8 mb-6">
              <div className="max-w-2xl">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by center name, state, or location..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl text-slate-900 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 border border-slate-200 bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Centers List */}
            <div className="px-4 lg:px-8 pb-8">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-cyan-600 border-r-transparent"></div>
                    <p className="mt-4 text-slate-600 text-lg">Loading centers...</p>
                  </div>
                </div>
              ) : filteredCenters.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                  <BuildingOffice2Icon className="mx-auto h-24 w-24 text-slate-300" />
                  <h3 className="mt-6 text-2xl font-semibold text-slate-900">No centers found</h3>
                  <p className="mt-2 text-slate-600">
                    {search ? "Try adjusting your search" : "No centers are registered yet"}
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-6 flex items-center justify-between">
                    <p className="text-slate-600">
                      Showing <span className="font-semibold text-slate-900">{filteredCenters.length}</span> center{filteredCenters.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div className="space-y-2">
                    {filteredCenters.map(center => (
                      <div
                        key={center._id}
                        className="bg-white rounded-lg border border-slate-200 hover:border-cyan-500 hover:shadow-md transition-all duration-200 cursor-pointer group"
                        onClick={() => navigate(`/centers/${center._id}`)}
                      >
                        <div className="p-4 flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors">
                            {center.organization}
                          </h3>
                          <ArrowRightIcon className="w-5 h-5 text-slate-400 group-hover:text-cyan-600 transition-colors" />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <MobileBottomNav />
    </div>
  )
}

export default CentersListPage
